import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Checkbox from '@atlaskit/checkbox';
import { token } from '@atlaskit/tokens';
import { useBulkOpsEntryPoint } from '@atlassian/jira-bulk-operations-async-core/src/utils.tsx';
import { CheckboxState } from '@atlassian/jira-bulk-operations-core/src/common/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { setCheckboxSelectionMode } from '../../../state/actions/cards/checkbox-selection-mode/index.tsx';
import {
	cardMultiSelectExtend,
	cardMultiRemove,
	cardEmptySelection,
} from '../../../state/actions/cards/selection/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { isCheckboxSelectionMode } from '../../../state/selectors/app/index.tsx';
import {
	getSelectedCards,
	makeOrderedVisibleCardsForCardList,
} from '../../../state/selectors/cards/index.tsx';
import { messages } from './messages.tsx';
import type { CardListCheckboxProps } from './types.tsx';
import { getCardListCheckboxState } from './utils.tsx';

export const useSelectedCards = () => useBacklogSelector(getSelectedCards);
export const useSelectableCards = (cardListId: string) =>
	useBacklogSelector((state) => makeOrderedVisibleCardsForCardList(state)(cardListId));
export const useSelectionMode = () => useBacklogSelector(isCheckboxSelectionMode);

const staticEntryPointActions = {
	load: noop,
};

export const CardListCheckbox = ({ cardListId, isDisabled, listName }: CardListCheckboxProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { entryPointActions } = expVal('bulk_operations_floating_toolbar', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBulkOpsEntryPoint()
		: { entryPointActions: staticEntryPointActions };

	const selectedCards = useSelectedCards();
	const selectableCards = useSelectableCards(cardListId);
	const selectionMode = useSelectionMode();

	const selectableIssueIds = useMemo(
		() => selectableCards.map((card) => card.id),
		[selectableCards],
	);

	const selectedCardsSet = useMemo(() => new Set(selectedCards), [selectedCards]);

	const selectedIssueIds = useMemo(
		() => selectableIssueIds.filter((id) => selectedCardsSet.has(id)),
		[selectableIssueIds, selectedCardsSet],
	);

	const extraAnalyticsAttributes = useMemo(
		() => ({
			selectAll: {
				checkbox: {
					issue_ids: selectableIssueIds,
					numSelectedCards: selectableIssueIds.length,
					selectType: 'sprint',
					isCheckboxChecked: true,
				},
			},
			clearAll: {
				checkbox: {
					issue_ids: [],
					numSelectedCards: 0,
					selectType: 'sprint',
					isCheckboxChecked: false,
				},
			},
		}),
		[selectableIssueIds],
	);

	const dispatch = useBacklogDispatch();

	const onSelectAll = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'changed',
			actionSubject: 'checkbox',
		});

		dispatch(cardMultiSelectExtend(selectableIssueIds));
		dispatch(setCheckboxSelectionMode(true));
		fireUIAnalytics(analyticsEvent, 'bulkSelectCardList', {
			...extraAnalyticsAttributes.selectAll.checkbox,
		});
	}, [
		createAnalyticsEvent,
		dispatch,
		extraAnalyticsAttributes.selectAll.checkbox,
		selectableIssueIds,
	]);

	const onClearAll = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'changed',
			actionSubject: 'checkbox',
		});

		dispatch(cardMultiRemove(selectedIssueIds));
		if (selectedCards.length === selectedIssueIds.length) {
			dispatch(setCheckboxSelectionMode(false));
		}

		fireUIAnalytics(analyticsEvent, 'bulkSelectCardList', {
			...extraAnalyticsAttributes.clearAll.checkbox,
		});
	}, [
		createAnalyticsEvent,
		dispatch,
		extraAnalyticsAttributes.clearAll.checkbox,
		selectedCards.length,
		selectedIssueIds,
	]);

	const checkboxState = useMemo(
		() =>
			getCardListCheckboxState({
				selectedIssueIds,
				selectableIssueIds,
				selectionMode,
			}),
		[selectedIssueIds, selectableIssueIds, selectionMode],
	);

	const onCheckboxClick = useCallback(
		(event: React.MouseEvent<HTMLInputElement>) => {
			event.stopPropagation();

			if (checkboxState === CheckboxState.CHECKED) {
				onClearAll();
			} else if (
				checkboxState === CheckboxState.UNCHECKED ||
				checkboxState === CheckboxState.INDETERMINATE
			) {
				if (!selectionMode && fg('tanuki_backlog_checkbox_selection')) {
					dispatch(cardEmptySelection());
				}

				expVal('bulk_operations_floating_toolbar', 'isEnabled', false) && entryPointActions.load();
				onSelectAll();
			}
		},
		[selectionMode, checkboxState, dispatch, onClearAll, entryPointActions, onSelectAll],
	);

	const { formatMessage } = useIntl();

	const getLabel = useMemo(() => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return checkboxState === CheckboxState.CHECKED
				? formatMessage(messages.deselectCheckboxLabelIssueTermRefresh, {
						listName,
					})
				: formatMessage(messages.selectCheckboxLabelIssueTermRefresh, {
						listName,
					});
		}
		return checkboxState === CheckboxState.CHECKED
			? formatMessage(messages.deselectCheckboxLabel, {
					listName,
				})
			: formatMessage(messages.selectCheckboxLabel, {
					listName,
				});
	}, [checkboxState, formatMessage, listName]);

	if (isVisualRefreshEnabled()) {
		return (
			<Checkbox
				testId="software-backlog.card-list.cardlist-checkbox.checkbox"
				onClick={onCheckboxClick}
				isChecked={
					checkboxState === CheckboxState.CHECKED || checkboxState === CheckboxState.INDETERMINATE
				}
				isIndeterminate={checkboxState === CheckboxState.INDETERMINATE}
				isDisabled={isDisabled || selectableIssueIds.length === 0}
				aria-label={getLabel}
				aria-disabled={isDisabled || selectableIssueIds.length === 0}
			/>
		);
	}

	return (
		<StyledCardListCheckbox data-testid="software-backlog.card-list.cardlist-checkbox.checkbox">
			<Checkbox
				onClick={onCheckboxClick}
				isChecked={
					checkboxState === CheckboxState.CHECKED || checkboxState === CheckboxState.INDETERMINATE
				}
				isIndeterminate={checkboxState === CheckboxState.INDETERMINATE}
				isDisabled={isDisabled || selectableIssueIds.length === 0}
				aria-label={getLabel}
				aria-disabled={isDisabled || selectableIssueIds.length === 0}
			/>
		</StyledCardListCheckbox>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCardListCheckbox = styled.div({
	paddingLeft: token('space.100', '8px'),
	// Align sprint container chevron to issue chevrons
	width: '20px',
	// Extra 1px of border here to help align this checkbox with the hardcoded padding in the card-contents component
	borderWidth: '1px',
	borderStyle: 'solid',
	borderColor: 'transparent',
});
